import * as React from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TwoFactorVerifyRequest } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { api, ApiRequestError } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const twoFactorSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/2fa/setup',
  component: TwoFactorSetupPage,
});

type FormValues = { code: string };

function TwoFactorSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const setupQuery = useQuery({
    queryKey: ['2fa', 'setup'],
    queryFn: () => api.startTwoFactorSetup(),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const verifyMutation = useMutation({
    mutationFn: (body: FormValues) => api.verifyTwoFactor(body),
    onSuccess: async () => {
      await navigate({ to: '/' });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(TwoFactorVerifyRequest),
    defaultValues: { code: '' },
  });

  const onSubmit = (values: FormValues): void => {
    setErrorMessage(null);
    verifyMutation.mutate(values);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          Set up two-factor sign-in.
        </h1>
        <p className="mt-3 text-base text-[hsl(var(--muted-foreground))]">
          Admin and Finance roles require an authenticator app. Scan the QR code below, then enter
          the 6-digit code to confirm.
        </p>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Scan with your authenticator</CardTitle>
            <CardDescription>
              Google Authenticator, 1Password, Bitwarden, and Authy all work.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {setupQuery.isLoading ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Preparing your secret...</p>
            ) : setupQuery.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not start 2FA setup</AlertTitle>
                <AlertDescription>
                  {setupQuery.error instanceof ApiRequestError
                    ? setupQuery.error.message
                    : 'Unexpected error.'}
                </AlertDescription>
              </Alert>
            ) : setupQuery.data ? (
              <div className="flex flex-col items-start gap-4">
                <img
                  src={setupQuery.data.qrPngDataUrl}
                  alt="Authenticator QR code"
                  className="size-48 rounded-xl border border-[hsl(var(--border))] bg-white p-2"
                />
                <div className="w-full">
                  <Label htmlFor="uri">Or paste this URI manually</Label>
                  <Input
                    id="uri"
                    readOnly
                    value={setupQuery.data.otpauthUri}
                    className="mt-2 font-mono text-xs"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {errorMessage ? (
          <Alert variant="destructive" className="mt-6">
            <AlertTitle>Invalid code</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="code">Verification code</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              {...form.register('code')}
            />
            {form.formState.errors.code ? (
              <p className="text-xs text-[hsl(var(--destructive))]">
                {form.formState.errors.code.message}
              </p>
            ) : null}
          </div>
          <Button type="submit" size="lg" disabled={verifyMutation.isPending}>
            {verifyMutation.isPending ? 'Verifying...' : 'Verify and continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
